import { inject, Injectable } from '@angular/core';
import { first, Observable, ReplaySubject, from, of, throwError } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { PrintDocumentDialogComponent } from 'src/app/components/print-document-dialog/print-document-dialog.component';
import { ProcessingDocumentDialogComponent } from 'src/app/components/processing-document-dialog/processing-document-dialog.component';
import { ReportUrlService } from './report-url.service';
import { ReportType } from './model/reportType.model';
import { InformalDialogOptions, PopUpDialogInformalService } from './confirms/popUpDialogInformal.service';
import { PopUpInformalType } from '../components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model';
import { LoginService } from './login.service';
import { GetReportingServicesGetPdfRequest, IReportParameter, ReportingServicesOutputMode, IExecuteProcedureResult, IReportingServicesGetPdfResultData, ResultCode, SendPdfRequest } from './model/pos.api.model';
import { SharedApiService } from './api/sharedApi.service';
import { ProcedureNameEnum } from '../enums/procedure-name.enum';
import { DbSchemeEnum } from '../enums/db-scheme.enum';

type ReportExportWorkflowOptions = {
  onReady?: () => void;
  onComplete?: () => void;
  showProcessingDialog?: boolean;
  reportType?: ReportType;
};

@Injectable({
  providedIn: 'root',
})

export class ReportExportService {
  private readonly reportOrigin: string;
  private iframeReadySubject = new ReplaySubject<HTMLIFrameElement>(1);
  private iframeElement?: HTMLIFrameElement;
  private processingDialogRef?: MatDialogRef<ProcessingDocumentDialogComponent>;

  private loginService = inject(LoginService);
  private sharedApiService = inject(SharedApiService);

  constructor(private dialog: MatDialog, private reportUrlService: ReportUrlService, private popupService: PopUpDialogInformalService, private translateService: TranslateService) {
    this.reportOrigin = this.reportUrlService.getReportOrigin();
    window.addEventListener('message', this.messageListener);
  }

  private messageListener = (event: MessageEvent) => {
    if (event.origin !== this.reportOrigin) {
      return;
    }

    if (event.data?.action === 'exportPdfReady' && typeof event.data.pdfUrl === 'string') {
      this.handleExportReady(event.data.pdfUrl);
    }
  };

  requestExportFromIframe(
    iframe?: HTMLIFrameElement | null,
    options?: ReportExportWorkflowOptions
  ): boolean {
    const iframeToUse = iframe ?? this.iframeElement;
    if (!iframeToUse) {
      return false;
    }

    const dispatchExport = () => {
      this.cleanupProcessingDialog();
      if (options?.showProcessingDialog) {
        this.processingDialogRef = this.dialog.open(ProcessingDocumentDialogComponent, {
          disableClose: true,
        });
      }
      iframeToUse.contentWindow?.postMessage({ action: 'exportPdf' }, this.reportOrigin);
      this.emitIframeReady(iframeToUse);
    };

    if (iframeToUse.contentWindow) {
      dispatchExport();
    } else {
      iframeToUse.addEventListener('load', dispatchExport, { once: true });
    }

    return true;
  }

  private handleExportReady(pdfUrl: string): void {
    this.cleanupProcessingDialog();
    const updatedUrl = this.normalizePdfUrl(pdfUrl);
    this.showPrintDialog(updatedUrl);
    this.cleanupIframeElement();
  }

  private normalizePdfUrl(pdfUrl: string): string {
    let formattedUrl = pdfUrl.replace(
      /ContentDisposition=OnlyHtmlInline/g,
      'ContentDisposition=AlwaysInline'
    );
    formattedUrl += '&rc:PrintOnOpen=false&rc:PageWidth=210mm&rc:PageHeight=297mm';
    return `${this.reportOrigin}${formattedUrl}`;
  }

  private showPrintDialog(url: string): void {
    const dialogRef = this.dialog.open(PrintDocumentDialogComponent, {
      width: '360px',
      maxWidth: '90vw',
      panelClass: 'print-document-dialog-panel',
      disableClose: true,
    });

    dialogRef.componentInstance.url = url;
  }

  public async buildUpIFrame(headerID: number, reportType: ReportType): Promise<HTMLIFrameElement> {
    const headerIdToUse = Number.isFinite(headerID) && headerID > 0 ? headerID : 0;
    const currentBusinessUnitID = this.loginService.user.SelectedBusinessUnit || null;
    const src = this.reportUrlService.getReportUrl(reportType, { HeaderID: headerIdToUse /* BUInternalNumber: currentBusinessUnitID */ });

    // If src is null or empty, do not create iframe
    if (!src) {
      // show alert dialog that report type is not supported
      console.error(
        'Cannot build report iframe: Invalid report URL for report type',
        reportType,
      );
      // Show popup dialog with error to user
      const title = this.translateService.instant('webPos.Errors.ReportExportError');
      const message = this.translateService.instant('webPos.Errors.ReportTypeNotSupported', { reportType });
      
      await this.popupService.confirm({
        title: title,
        message: message,
        type: PopUpInformalType.Error,
      } as InformalDialogOptions);
      

      this.cleanupIframeElement();
      return null!;
      
    }

    this.cleanupIframeElement();
    this.iframeReadySubject = new ReplaySubject<HTMLIFrameElement>(1);

    const iframe = document.createElement('iframe');
    iframe.name = 'report-iframe';
    iframe.style.position = 'absolute';
    iframe.style.top = '-40000px';
    iframe.src = src;
    iframe.addEventListener(
      'load',
      () => {
        console.log('Report iframe loaded');
        setTimeout(() => this.emitIframeReady(iframe), 500);
      },
      { once: true }
    );
    document.body.appendChild(iframe);
    this.iframeElement = iframe;
    return iframe;
  }

  iframeReady(): Observable<HTMLIFrameElement> {
    return this.iframeReadySubject.asObservable();
  }

  public async triggerPrintWorkflow(
    headerID: number | string,
    options?: ReportExportWorkflowOptions
  ): Promise<void> {
    const headerIDparsed = typeof headerID === 'number' ? headerID : Number(headerID);

    if (!Number.isFinite(headerIDparsed) || headerIDparsed <= 0) {
      console.error('Invalid HeaderID provided for report export:', headerID);
      return;
    }

    this.cleanupProcessingDialog();
    if (options?.showProcessingDialog) {
      this.processingDialogRef = this.dialog.open(ProcessingDocumentDialogComponent, {
        disableClose: true,
      });
    }

    const iframe = await this.buildUpIFrame(headerIDparsed, options?.reportType || ReportType.transaction);
    this.iframeReady()
      .pipe(first())
      .subscribe(async () => {
        options?.onReady?.();
        await this.requestExportFromIframe(iframe);
        options?.onComplete?.();
      });
  }

  private emitIframeReady(iframe: HTMLIFrameElement): void {
    this.iframeReadySubject.next(iframe);
  }

  private cleanupIframeElement(): void {
    if (this.iframeElement) {
      this.iframeElement.remove();
      this.iframeElement = undefined;
    }
  }

  private cleanupProcessingDialog(): void {
    this.processingDialogRef?.close();
    this.processingDialogRef = undefined;
  }

  /**
   * Download PDF report using ReportingServicesGetPDF API
   * Retrieves PDF as base64-encoded JSON, converts to blob, and triggers print dialog
   * 
   * @param reportName - SSRS report path (e.g., '/NG/CS/NG_sub_cash_closing_a4'). Optional if HeaderID is provided in params - will be auto-determined.
   * @param params - Optional report parameters. If HeaderID is provided, report name will be auto-determined.
   * @param showProcessingDialog - Show processing dialog during download
   * @returns Promise that resolves when print dialog is shown or error occurs
   */
  public async downloadPdf(
    reportName?: string,
    params?: IReportParameter[],
    showProcessingDialog: boolean = true
  ): Promise<void> {
    this.cleanupProcessingDialog();

    if (showProcessingDialog) {
      this.processingDialogRef = this.dialog.open(ProcessingDocumentDialogComponent, {
        disableClose: true,
      });
    }

    // Create request for JSON mode (returns base64-encoded PDF)
    const request = new GetReportingServicesGetPdfRequest(
      reportName || '',
      params || [],
      ReportingServicesOutputMode.Json
    );

    // Execute procedure to get PDF as JSON using RxJS error handling
    const pdfResponse$ = from(
      this.sharedApiService.executeProcedure(request) as Promise<IExecuteProcedureResult<IReportingServicesGetPdfResultData>>
    ).pipe(
      tap(() => this.cleanupProcessingDialog()),
      tap(response => {
        console.log('[downloadPdf] Response received:', response?.result);
        if (!response || response.result !== ResultCode.OK) {
          throw new Error('Failed to download PDF');
        }
        if (!response.data || !response.data.success) {
          throw new Error(response.data?.error || 'PDF generation failed');
        }
        if (!response.data.pdfData) {
          throw new Error('No PDF content');
        }
      }),
      catchError(error => {
        console.error('[downloadPdf] Error downloading PDF:', error);
        this.cleanupProcessingDialog();
        const errorMsg = error instanceof Error ? error.message : this.translateService.instant('webPos.Errors.UnexpectedError');
        return from(this.showErrorDialog(
          this.translateService.instant('webPos.Errors.Error'),
          errorMsg
        )).pipe(
          switchMap(() => throwError(() => error))
        );
      })
    );

    const response = await lastValueFrom(pdfResponse$).catch(() => null);

    if (!response?.data?.pdfData) {
      return;
    }

    // Convert base64 to blob
    const blob = this.base64ToBlob(response.data.pdfData, 'application/pdf');

    // Show print dialog if showProcessingDialog was true (PDF was downloaded for printing)
    if (showProcessingDialog) {
      const blobUrl = URL.createObjectURL(blob);
      this.showPrintDialog(blobUrl);
    }
  }

  /**
   * Convert base64 string to Blob
   * 
   * @param base64String - Base64-encoded data
   * @param mimeType - MIME type for the blob (default: 'application/octet-stream')
   * @returns Blob object
   */
  private base64ToBlob(base64String: string, mimeType: string = 'application/octet-stream'): Blob {
    // Decode base64 string to character array
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    // Convert character array to byte array
    const byteArray = new Uint8Array(byteNumbers);

    // Create and return blob
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * Show error dialog to user
   * 
   * @param title - Dialog title
   * @param message - Dialog message
   */
  private async showErrorDialog(title: string, message: string): Promise<void> {
    await this.popupService.confirm({
      title: title,
      message: message,
      type: PopUpInformalType.Error,
    } as InformalDialogOptions);
  }

  /**
   * Send PDF receipt via email using SendPdf procedure
   * Independent operation not tied to downloadPdf - called separately when user confirms email sending
   * 
   * @param targetEmail - Recipient email address (must be pre-validated)
   * @param headerId - Header ID for the PDF to send (BIGINT)
   * @returns Promise that resolves to true if email sent successfully, false otherwise
   */
  public async sendPdfByEmail(targetEmail: string, headerId: number): Promise<boolean> {
    console.log('[sendPdfByEmail] Starting with email:', targetEmail, 'headerId:', headerId);

    const request = new SendPdfRequest(targetEmail, headerId);
    console.log('[sendPdfByEmail] Creating SendPdfRequest:', request);

    const emailResult$ = from(
      this.sharedApiService.executeProcedure(request) as Promise<IExecuteProcedureResult<any>>
    ).pipe(
      tap(response => console.log('[sendPdfByEmail] Response received:', response)),
      map(response => {
        if (!response || response.result !== ResultCode.OK) {
          throw new Error('Procedure execution failed');
        }
        console.log('[sendPdfByEmail] Email sent successfully');
        return true;
      }),
      catchError(error => {
        console.error('[sendPdfByEmail] Error occurred:', error);
        const errorMessage = error instanceof Error ? error.message : this.translateService.instant('webPos.Errors.EmailSendFailed');
        return from(
          this.showErrorDialog(
            this.translateService.instant('webPos.Errors.Error'),
            errorMessage
          )
        ).pipe(
          map(() => false),
          catchError(() => of(false))
        );
      })
    );

    return lastValueFrom(emailResult$).catch(() => false);
  }

}
